import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Typography,
  TextField,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  PeopleAlt,
  FlashOn,
  AttachMoney,
  AccessTime,
  BoltOutlined,
  Phone,
  Email,
  DirectionsCar,
  LocationOn,
} from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { ReportCard } from '../shared/ReportCard';
import { useReportData } from '../../../hooks/useReportData';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

type UserType = 'app' | 'rfid' | 'vendor_api' | 'backend' | 'kiosk';
const ALL_USER_TYPES: UserType[] = ['app', 'rfid', 'vendor_api', 'backend', 'kiosk'];

const USER_TYPE_CHIP_COLOR: Record<UserType, 'primary' | 'secondary' | 'info' | 'warning' | 'success'> = {
  app: 'primary',
  rfid: 'secondary',
  vendor_api: 'info',
  backend: 'warning',
  kiosk: 'success',
};

const USER_TYPE_FALLBACK: Record<UserType, string> = { app: 'App', rfid: 'RFID', vendor_api: 'Vendor API', backend: 'Backend', kiosk: 'Kiosk' };

interface UserRow {
  user_id: string;
  user_type: UserType;
  name: string;
  phone: string;
  email: string;
  car_plate: string | null;
  language: string | null;
  total_sessions: number;
  charged_sessions: number;
  total_duration_seconds: number;
  total_energy_kwh: number;
  total_revenue: number;
  first_session_at: string | null;
  last_session_at: string | null;
  sites_visited: string[];
  sites_visited_count: number;
}

interface UserActivityData {
  summary: {
    total_users: number;
    total_sessions: number;
    charged_sessions: number;
    total_duration_seconds: number;
    total_energy_kwh: number;
    total_revenue: number;
    avg_sessions_per_user: number;
    avg_revenue_per_user: number;
  };
  users: UserRow[];
  total: number;
  limit: number;
  offset: number;
}

type SortKey = 'total_revenue' | 'total_sessions' | 'total_energy' | 'last_session';

// Format a duration in seconds as "Nh Mm" — compact, human-readable.
const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Grab the auth token the same way the other report components do. The shared
// dataProvider flow wraps GET responses in React Admin's list shape, which
// doesn't fit CSV downloads — for export we hit the backend directly.
const getAuthHeaders = (): Record<string, string> | null => {
  const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
  const token = tokenData ? JSON.parse(tokenData).accessToken : null;
  if (!token) return null;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export const UserActivityReport: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filter state.
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [chargerId, setChargerId] = useState('');

  // User-type multi-select. Default to all four buckets — the backend
  // accepts a comma-separated subset and treats empty as "all".
  const [userTypes, setUserTypes] = useState<UserType[]>([...ALL_USER_TYPES]);

  // Search is a free-text field over name/phone/car_plate; debounced so we
  // don't slam the API on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Filter option state — populated from the shared report-filters endpoints,
  // which already respect vendor scope.
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [chargers, setChargers] = useState<Array<{ id: string; name: string }>>([]);

  // Sort + pagination. Reset offset whenever a filter or sort changes so the
  // user doesn't stay on page 4 of a result set that's now 2 pages total.
  const [sortKey, setSortKey] = useState<SortKey>('total_revenue');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, siteId, vendorId, chargerId, search, sortKey, order, userTypes]);

  const userTypesParam = useMemo(() => {
    if (userTypes.length === 0 || userTypes.length === ALL_USER_TYPES.length) return '';
    return userTypes.join(',');
  }, [userTypes]);

  const [exporting, setExporting] = useState(false);

  // Filter option fetches — mirror SessionAnalyticsReport to stay consistent.
  useEffect(() => {
    (async () => {
      const headers = getAuthHeaders();
      if (!headers) return;
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await res.json();
        setVendors(data.data || []);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const headers = getAuthHeaders();
      if (!headers) return;
      try {
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;
        const res = await fetch(url, { headers });
        const data = await res.json();
        setSites(data.data || []);
        // If the previously-selected site falls outside the new list, drop it.
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (err) {
        console.error('Failed to fetch sites:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  useEffect(() => {
    (async () => {
      const headers = getAuthHeaders();
      if (!headers) return;
      try {
        let url = `${API_CONFIG.baseURL}/api/backend/reports/filters/chargers`;
        const params = new URLSearchParams();
        if (siteId) params.append('site_id', siteId);
        else if (vendorId) params.append('vendor_id', vendorId);
        if (params.toString()) url += `?${params.toString()}`;

        const res = await fetch(url, { headers });
        const data = await res.json();
        setChargers(data.data || []);
        if (chargerId && !data.data?.some((c: any) => c.id === chargerId)) {
          setChargerId('');
        }
      } catch (err) {
        console.error('Failed to fetch chargers:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, siteId]);

  // The JSON endpoint paginates server-side — ask for the current page's
  // worth of rows. Summary comes back pre-aggregated over the full set.
  const offset = page * rowsPerPage;
  const { data, loading, error, refetch } = useReportData<UserActivityData>({
    endpoint: '/api/backend/reports/user-activity',
    filters: {
      start_date: startDate,
      end_date: endDate,
      sort: sortKey,
      order,
      limit: rowsPerPage,
      offset,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
      ...(chargerId && { charger_id: chargerId }),
      ...(search && { search }),
      ...(userTypesParam && { user_types: userTypesParam }),
    },
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Default to desc for numeric "biggest first"; asc for last_session
      // doesn't make sense ("oldest first") so default that to desc too.
      setOrder('desc');
    }
  };

  // Direct export via the backend /export endpoint. We don't use the shared
  // useReportExport hook because the backend already emits CSV server-side
  // with the full (unpaginated) result set and consistent scoping — letting
  // the frontend rebuild CSV from the current page would silently truncate.
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        throw new Error('No auth token');
      }

      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        sort: sortKey,
        order,
      });
      if (siteId) params.append('site_id', siteId);
      if (vendorId) params.append('vendor_id', vendorId);
      if (chargerId) params.append('charger_id', chargerId);
      if (search) params.append('search', search);
      if (userTypesParam) params.append('user_types', userTypesParam);

      const res = await fetch(
        `${API_CONFIG.baseURL}/api/backend/reports/user-activity/export?${params.toString()}`,
        { headers },
      );
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);

      const truncated = res.headers.get('X-Export-Truncated');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename from Content-Disposition when available; otherwise fall
      // back to a sensible default.
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || `user-activity-${startDate}-to-${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Toast on success so the user gets the same feedback as the other
      // operational reports (which use useReportExport's built-in toasts).
      notify(
        translate('reports.export.csvSuccess', { fallback: 'CSV exported successfully' }),
        { type: 'success' },
      );

      if (truncated) {
        notify(
          translate('reports.userActivity.exportTruncated', {
            fallback: `Export was truncated to the first ${truncated} rows. Narrow the date range to see more.`,
            count: truncated,
          }),
          { type: 'warning', autoHideDuration: 8000 },
        );
      }
    } catch (err) {
      console.error('Export failed:', err);
      notify(
        translate('reports.userActivity.exportFailed', { fallback: 'Export failed — see console.' }),
        { type: 'error' },
      );
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const users = data?.users || [];
  const total = data?.total || 0;

  // Desktop table — sortable per column via TableSortLabel.
  const columnHeader = (key: SortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? order : 'desc'}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <ReportLayout
      title={translate('reports.userActivity.title', { fallback: 'User Activity' })}
      description={translate('reports.userActivity.description', {
        fallback:
          'Charging activity aggregated per user (App, RFID, Vendor API, Backend) across your sites — sessions, duration, energy, revenue, and contact info for follow-up.',
      })}
      exportButtons={{
        onExportCSV: handleExportCSV,
        exporting,
        disabled: loading || !!error,
      }}
      loading={loading}
      error={error}
      filters={
        <Box>
          <ReportFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            vendorId={vendorId}
            onVendorIdChange={setVendorId}
            siteId={siteId}
            onSiteIdChange={setSiteId}
            chargerId={chargerId}
            onChargerIdChange={setChargerId}
            showVendorFilter={vendors.length > 0}
            showSiteFilter
            showChargerFilter
            onRefresh={refetch}
            sites={sites}
            vendors={vendors}
            chargers={chargers}
          />
          {/* Search row + user-type multi-select — both sit below the shared
              filter bar since they don't belong in the dropdown group. */}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField
              label={translate('reports.userActivity.searchLabel', {
                fallback: 'Search user (name / phone)',
              })}
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={translate('reports.userActivity.searchPlaceholder', {
                fallback: 'Type a name or phone',
              })}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 280 } }}>
              <InputLabel id="user-activity-user-types-label">
                {translate('reports.userActivity.userTypesLabel', { fallback: 'User types' })}
              </InputLabel>
              <Select<UserType[]>
                labelId="user-activity-user-types-label"
                multiple
                value={userTypes}
                onChange={(e: SelectChangeEvent<UserType[]>) => {
                  const value = e.target.value;
                  const next = (typeof value === 'string' ? value.split(',') : value) as UserType[];
                  // Don't let the user end up with zero selected — empty
                  // selection would silently revert to "all" via the
                  // userTypesParam derivation. Keep the previous state.
                  if (next.length === 0) return;
                  setUserTypes(next);
                }}
                input={<OutlinedInput label={translate('reports.userActivity.userTypesLabel', { fallback: 'User types' })} />}
                renderValue={(selected) => {
                  const arr = selected as UserType[];
                  if (arr.length === ALL_USER_TYPES.length) {
                    return translate('reports.userActivity.userTypeAll', { fallback: 'All' });
                  }
                  return arr
                    .map((t) =>
                      translate(`reports.userActivity.userType.${t}`, {
                        fallback: USER_TYPE_FALLBACK[t],
                      })
                    )
                    .join(', ');
                }}
              >
                {ALL_USER_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    <Checkbox checked={userTypes.includes(t)} />
                    <ListItemText
                      primary={translate(`reports.userActivity.userType.${t}`, {
                        fallback: USER_TYPE_FALLBACK[t],
                      })}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      }
    >
      {/* Summary cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <ReportCard
          title={translate('reports.userActivity.kpi.totalUsers', { fallback: 'Total Users' })}
          value={formatNumber(summary?.total_users ?? 0, 0)}
          subtitle={translate('reports.userActivity.kpi.totalUsersSubtitle', {
            fallback: 'Active users',
          })}
          icon={PeopleAlt}
          color="#009A75"
          loading={loading}
        />
        {canSeeTotalSessions && (
          <ReportCard
            title={translate('reports.userActivity.kpi.totalSessions', { fallback: 'Total Sessions' })}
            value={formatNumber(summary?.total_sessions ?? 0, 0)}
            subtitle={translate('reports.userActivity.kpi.avgPerUser', {
              fallback: `${formatNumber(summary?.avg_sessions_per_user ?? 0, 1)} avg/user`,
              count: formatNumber(summary?.avg_sessions_per_user ?? 0, 1),
            })}
            icon={AccessTime}
            color="#2196F3"
            loading={loading}
            tooltip={translate('reports.kpi.totalSessionsTooltip', {
              fallback: 'All archived sessions in the selected date range, including those that delivered no energy.',
            })}
          />
        )}
        <ReportCard
          title={translate('reports.userActivity.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
          value={formatNumber(summary?.charged_sessions ?? 0, 0)}
          icon={BoltOutlined}
          color="#7E57C2"
          loading={loading}
          tooltip={translate('reports.kpi.chargedSessionsTooltip', {
            fallback: 'Sessions that actually delivered energy (kWh > 0). Sessions where the vehicle plugged in but no energy was delivered (for example, immediate unplug or fault) are excluded.',
          })}
        />
        <ReportCard
          title={translate('reports.userActivity.kpi.totalEnergy', { fallback: 'Total Energy' })}
          value={`${formatNumber(summary?.total_energy_kwh ?? 0, 2)} kWh`}
          icon={FlashOn}
          color="#FFB800"
          loading={loading}
        />
        <ReportCard
          title={translate('reports.userActivity.kpi.totalRevenue', { fallback: 'Total Revenue' })}
          value={`HKD ${formatNumber(summary?.total_revenue ?? 0, 2)}`}
          subtitle={translate('reports.userActivity.kpi.avgRevenuePerUser', {
            fallback: `HKD ${formatNumber(summary?.avg_revenue_per_user ?? 0, 2)} avg/user`,
            amount: formatNumber(summary?.avg_revenue_per_user ?? 0, 2),
          })}
          icon={AttachMoney}
          color="#4CAF50"
          loading={loading}
        />
      </Box>

      {/* User table */}
      <Card sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {isMobile ? (
            // Mobile view — one card per user. Desktop table is dense; cards
            // let us show the same fields without horizontal scroll pain.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {users.length === 0 && !loading && (
                <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {translate('reports.userActivity.empty', { fallback: 'No users match the current filters.' })}
                </Typography>
              )}
              {users.map((u) => (
                <Paper key={`${u.user_type}:${u.user_id}`} sx={{ p: 2 }} variant="outlined">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }}>{u.name}</Typography>
                      <Chip
                        size="small"
                        color={USER_TYPE_CHIP_COLOR[u.user_type]}
                        label={translate(`reports.userActivity.userType.${u.user_type}`, {
                          fallback: USER_TYPE_FALLBACK[u.user_type],
                        })}
                      />
                    </Box>
                    <Chip
                      label={`HKD ${formatNumber(u.total_revenue, 2)}`}
                      size="small"
                      sx={{ backgroundColor: '#4CAF5015', color: '#4CAF50', fontWeight: 600 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                    {u.phone && (
                      <Tooltip title={translate('reports.userActivity.fields.phone', { fallback: 'Phone' })}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="caption">{u.phone}</Typography>
                        </Box>
                      </Tooltip>
                    )}
                    {u.email && (
                      <Tooltip title={translate('reports.userActivity.fields.email', { fallback: 'Email' })}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{u.email}</Typography>
                        </Box>
                      </Tooltip>
                    )}
                    {u.car_plate && (
                      <Tooltip title={translate('reports.userActivity.fields.carPlate', { fallback: 'Car Plate' })}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DirectionsCar sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="caption">{u.car_plate}</Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {translate('reports.userActivity.fields.sessions', { fallback: 'Sessions' })}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {canSeeTotalSessions
                          ? `${formatNumber(u.total_sessions, 0)} / ${formatNumber(u.charged_sessions, 0)}`
                          : formatNumber(u.charged_sessions, 0)}
                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                          ({translate('reports.userActivity.table.chargedSessions', { fallback: 'charged' })})
                        </Typography>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {translate('reports.userActivity.fields.duration', { fallback: 'Duration' })}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDuration(u.total_duration_seconds)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {translate('reports.userActivity.fields.energy', { fallback: 'Energy' })}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatNumber(u.total_energy_kwh, 2)} kWh
                      </Typography>
                    </Box>
                  </Box>
                  {u.sites_visited.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <LocationOn sx={{ fontSize: 14, color: '#666' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {u.sites_visited.slice(0, 3).join(', ')}
                        {u.sites_visited_count > 3 && ` +${u.sites_visited_count - 3}`}
                      </Typography>
                    </Box>
                  )}
                  {u.last_session_at && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      {translate('reports.userActivity.fields.lastSession', { fallback: 'Last session' })}: {formatDate(u.last_session_at)}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
                      {translate('reports.userActivity.table.user', { fallback: 'User' })}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
                      {translate('reports.userActivity.table.type', { fallback: 'Type' })}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
                      {translate('reports.userActivity.table.contact', { fallback: 'Contact' })}
                    </TableCell>
                    {canSeeTotalSessions && columnHeader('total_sessions', translate('reports.userActivity.table.sessions', { fallback: 'Sessions' }))}
                    <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {translate('reports.userActivity.table.chargedSessions', { fallback: 'Charged Sessions' })}
                        <Tooltip
                          title={translate('reports.kpi.chargedSessionsTooltip', {
                            fallback: 'Sessions that actually delivered energy (kWh > 0).',
                          })}
                          arrow
                          placement="top"
                        >
                          <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E', display: 'inline-flex' }}>ⓘ</Box>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
                      {translate('reports.userActivity.table.duration', { fallback: 'Duration' })}
                    </TableCell>
                    {columnHeader('total_energy', translate('reports.userActivity.table.energy', { fallback: 'Energy (kWh)' }))}
                    {columnHeader('total_revenue', translate('reports.userActivity.table.revenue', { fallback: 'Revenue (HKD)' }))}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#F5F5F5' }}>
                      {translate('reports.userActivity.table.sites', { fallback: 'Sites' })}
                    </TableCell>
                    {columnHeader('last_session', translate('reports.userActivity.table.lastSession', { fallback: 'Last session' }), 'left')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {translate('reports.userActivity.empty', { fallback: 'No users match the current filters.' })}
                      </TableCell>
                    </TableRow>
                  )}
                  {users.map((u) => (
                    <TableRow key={`${u.user_type}:${u.user_id}`} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>
                        {u.car_plate && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {u.car_plate}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={USER_TYPE_CHIP_COLOR[u.user_type]}
                          label={translate(`reports.userActivity.userType.${u.user_type}`, {
                            fallback: USER_TYPE_FALLBACK[u.user_type],
                          })}
                        />
                      </TableCell>
                      <TableCell>
                        {u.phone && (
                          <Typography variant="caption" sx={{ display: 'block' }}>{u.phone}</Typography>
                        )}
                        {u.email && (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'text.secondary', wordBreak: 'break-all' }}
                          >
                            {u.email}
                          </Typography>
                        )}
                      </TableCell>
                      {canSeeTotalSessions && (
                        <TableCell align="right">{formatNumber(u.total_sessions, 0)}</TableCell>
                      )}
                      <TableCell align="right">{formatNumber(u.charged_sessions, 0)}</TableCell>
                      <TableCell align="right">{formatDuration(u.total_duration_seconds)}</TableCell>
                      <TableCell align="right">{formatNumber(u.total_energy_kwh, 2)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatNumber(u.total_revenue, 2)}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            u.sites_visited_count > u.sites_visited.length
                              ? `${u.sites_visited.join(', ')} (+${u.sites_visited_count - u.sites_visited.length})`
                              : u.sites_visited.join(', ')
                          }
                        >
                          <Typography variant="caption">
                            {u.sites_visited.slice(0, 2).join(', ')}
                            {u.sites_visited_count > 2 && ` +${u.sites_visited_count - 2}`}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {u.last_session_at ? formatDate(u.last_session_at) : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100, 200]}
          />
        </CardContent>
      </Card>
    </ReportLayout>
  );
};
